// src/components/pricing/PricingCard.jsx

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faRocket, faCrown } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';

const PricingCard = ({
    plan = 'Plan',
    price = { monthly: 0, annual: 0, currency: 'USD', features: [] },
    currency = 'USD',
    billingPeriod = 'monthly',
    features = [],
    onSelect = () => { },
    isCurrent = false,
    isLoggedIn = false,
}) => {
    // Asignar un valor predeterminado a 'currency' si está indefinido
    const displayCurrency = currency ? currency.toUpperCase() : 'USD';

    // Asignar un símbolo de moneda basado en 'currency'
    const currencySymbols = {
        EUR: '€',
        USD: '$',
        CLP: 'CLP$',
        PEN: 'S/',
    };
    const symbol = currencySymbols[displayCurrency] || '$';

    // Determinar si la moneda tiene subunidades
    const isSubunitCurrency = ['EUR', 'USD', 'PEN'].includes(displayCurrency);
    const formattedPrice = isSubunitCurrency
        ? ((price[billingPeriod] || 0) / 100).toFixed(2)
        : (price[billingPeriod] || 0).toFixed(0);

    const navigate = useNavigate();

    const handleRegister = () => {
        navigate('/register');
    };

    const handleSelect = () => {
        onSelect(plan);
    };

    // Determinar el color del plan
    const getPlanColor = () => {
        switch (plan.toLowerCase()) {
            case 'basic':
                return 'from-gray-500 to-gray-600';
            case 'pro':
                return 'from-blue-500 to-blue-600';
            case 'enterprise':
                return 'from-purple-500 to-purple-600';
            default:
                return 'from-blue-500 to-blue-600';
        }
    };

    // Determinar el icono del plan
    const getPlanIcon = () => {
        switch (plan.toLowerCase()) {
            case 'basic':
                return faRocket;
            case 'pro':
                return faCrown;
            case 'enterprise':
                return faCrown;
            default:
                return faRocket;
        }
    };

    return (
        <div className={`relative bg-white rounded-2xl shadow-lg overflow-hidden transform hover:scale-105 transition-all duration-300 border-2 ${isCurrent ? 'border-blue-500 shadow-2xl' : 'border-gray-100 hover:border-blue-300'
            }`}>
            {/* Badge para plan actual */}
            {isCurrent && (
                <div className="absolute top-4 right-4 bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    Plan Actual
                </div>
            )}

            {/* Header del plan */}
            <div className={`bg-gradient-to-r ${getPlanColor()} text-white p-8 text-center`}>
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <FontAwesomeIcon icon={getPlanIcon()} className="text-2xl" />
                </div>
                <h3 className="text-2xl font-bold mb-2 capitalize">{plan}</h3>
                <div className="text-4xl font-extrabold mb-2">
                    {symbol} {formattedPrice}
                </div>
                <div className="text-lg font-medium opacity-90">
                    /{billingPeriod === 'monthly' ? 'mes' : 'año'}
                </div>
            </div>

            {/* Contenido del plan */}
            <div className="p-8">
                {/* Lista de características */}
                <ul className="space-y-4 mb-8">
                    {features.map((feature, index) => (
                        <li key={index} className="flex items-start">
                            <div className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                                <FontAwesomeIcon icon={faCheck} className="text-green-600 text-xs" />
                            </div>
                            <span className="text-gray-700 leading-relaxed">{feature}</span>
                        </li>
                    ))}
                </ul>

                {/* Botón de acción */}
                {isCurrent ? (
                    <button
                        onClick={handleSelect}
                        className="w-full py-4 px-6 bg-blue-500 hover:bg-blue-600 text-white font-bold rounded-xl transition-colors duration-300 flex items-center justify-center"
                    >
                        <FontAwesomeIcon icon={faCheck} className="mr-2" />
                        Plan Actual
                    </button>
                ) : isLoggedIn ? (
                    <button
                        onClick={handleSelect}
                        className="w-full py-4 px-6 bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-blue-900 font-bold rounded-xl transition-all duration-300 flex items-center justify-center transform hover:scale-105"
                    >
                        <FontAwesomeIcon icon={faRocket} className="mr-2" />
                        Seleccionar Plan
                    </button>
                ) : (
                    <button
                        onClick={handleRegister}
                        className="w-full py-4 px-6 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold rounded-xl transition-all duration-300 flex items-center justify-center transform hover:scale-105"
                    >
                        <FontAwesomeIcon icon={faRocket} className="mr-2" />
                        Comenzar Prueba Gratis
                    </button>
                )}

                {/* Información adicional */}
                <div className="mt-6 text-center">
                    <p className="text-sm text-gray-500">
                        {isCurrent ? 'Tu plan actual' : 'Incluye prueba gratuita de 30 días'}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default PricingCard;
